// mbscan manager-client の同名部品と共通。片側だけで変えない
// useFilterBarState 系（use-filter-bar-state.ts・filter-bar-url-state.ts など）は mbscan manager-client へ同じ形で移植する予定（移植までは mbfab のみ）

import { isSome } from '@mbfab/shared'
import { ChevronDown, Search, SlidersHorizontal, X } from 'lucide-react'
import type { ReactNode } from 'react'
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import { FilterBarContext, FilterTextInput } from '@/components/ui/filter-text-input'
import { cn } from '@/lib/utils'

export { useFilterBarState } from '@/components/ui/use-filter-bar-state'

// 使い方のルール: docs/develop/client_ui/components/filter-bar.md

// 展開式の FilterBar は入力とトグルボタンの高さをこの1定数で揃える。手書きで別々に書くとズレるため
// （Button の className に h-* を直書きすると lint で落ちる。ここだけが例外。docs/develop/client_ui/components/button.md「size（高さを揃える）」）
const filterBarControlHeightClass = 'h-10'

// インライン表示のフリーワード欄の幅。隣に並ぶ絞り込み項目と同じ行に収めるため全幅にしない。
// 長めの placeholder が切れない幅
const inlineFreeWordWidthClass = 'sm:w-80'

export type FilterBarChip<Key extends string = string> = {
  key: Key
  label: string
  value: string
}

/**
 * 詳細絞り込みの出し方。
 * - 展開式（既定）: 「詳細絞り込み」トグルで children を開閉する。項目が多い画面向け。
 *   トグルの左には必ず何かを常時表示する: 複数カラムを横断するキーワード検索があれば `freeWord`、
 *   無ければ重要な項目を 2〜3 個 `primaryFilters` に置く（トグルだけがぽつんと出る形にしない）
 * - インライン（`expandable: false`）: トグルを置かず children をフリーワード欄と同じ行に常時表示する。
 *   項目が 3 つ程度までの画面向け。開閉状態を持たないので `expanded` 系の props は受け取らない
 */
type FilterBarExpansionProps =
  | {
      expandable?: true
      expanded: boolean
      onExpandedChange: (next: boolean) => void
      /** トグルボタンのバッジに出す、適用中の詳細条件の数（`primaryFilters` の条件は数えない） */
      detailFilterCount: number
      /**
       * トグルの左に常時表示する重要な絞り込み項目（打設日・打設箇所など）。キーワード検索が無い画面で使い、
       * `freeWord` とは併用しない（キーワード検索がある画面はフリーワード + 詳細絞り込みにする）。
       * FilterBar が flex 行に並べるので、各項目（`CompactFormField` 等）を直接並べ、幅は項目側の className で指定する
       */
      primaryFilters?: ReactNode
    }
  | {
      expandable: false
      expanded?: undefined
      onExpandedChange?: undefined
      detailFilterCount?: undefined
      primaryFilters?: undefined
    }

type FilterBarProps<Key extends string> = FilterBarExpansionProps & {
  /** 適用済みのフリーワード。入力中の文字列は FilterBar（FilterTextInput）の中で持つので、draft を渡さない */
  freeWord?: string
  /** 入力がひと段落したとき（FilterTextInput の待ち時間経過・Enter・クリア）にだけ呼ばれる。ここで一覧を再取得してよい */
  onFreeWordChange?: (next: string) => void
  freeWordPlaceholder?: string
  /**
   * フリーワード・詳細・主要項目の FilterTextInput のどれかが入力待ち（または変換中）になった／解消したときに呼ばれる。
   * 表示中の条件がまだ一覧に反映されていない間、一括編集などの操作を止めたい画面で使う
   */
  onPendingChange?: (pending: boolean) => void
  freeWordAriaLabel?: string
  /** フリーワード欄の上に出すラベル。インラインでラベル付きの項目と並べる画面向け（省略時はラベル無しで従来どおり） */
  freeWordLabel?: string
  chips: Array<FilterBarChip<Key>>
  onRemoveChip: (key: Key) => void
  onClearAll: () => void
  testIdPrefix: string
  /**
   * 詳細絞り込みの項目。展開式では呼び出し側が grid 等でレイアウトして渡す。
   * インラインでは FilterBar が flex 行に並べるので、各項目（`CompactFormField` 等）を直接並べ、幅は項目側の className で指定する
   */
  children?: ReactNode
  className?: string
}

/**
 * 一覧画面の「フリーワード + 詳細絞り込み + 適用中条件」を組み立てる共通シェル。
 * 条件の型・draft の確定方法・詳細フォームは画面固有なので、状態と children は呼び出し側が持つ。
 */
export function FilterBar<Key extends string>(props: FilterBarProps<Key>) {
  const {
    freeWord,
    onFreeWordChange,
    freeWordPlaceholder,
    onPendingChange,
    freeWordAriaLabel = 'フリーワード検索',
    freeWordLabel,
    chips,
    onRemoveChip,
    onClearAll,
    testIdPrefix,
    children,
    className,
  } = props
  const inline = props.expandable === false
  const primaryFilters = props.expandable === false ? undefined : props.primaryFilters
  // 常時表示の項目（ラベル + 入力の2段・compact 系 h-9）と同じ行に並べる配置。インラインと主要項目表示が該当する
  const compactRow = inline || isSome(primaryFilters)
  const freeWordInputId = `${testIdPrefix}-free-word-input`
  const hasDetail = isSome(children)
  const pendingIds = useRef(new Set<string>())
  const latestOnPendingChange = useRef(onPendingChange)
  // 描画中に ref を書き換えない（lint）。描画直後に同期的に反映するため useLayoutEffect を使う
  useLayoutEffect(() => {
    latestOnPendingChange.current = onPendingChange
  })
  const reportPending = useCallback((id: string, pending: boolean) => {
    const before = pendingIds.current.size > 0
    if (pending) {
      pendingIds.current.add(id)
    } else {
      pendingIds.current.delete(id)
    }
    const after = pendingIds.current.size > 0
    if (before !== after) {
      latestOnPendingChange.current?.(after)
    }
  }, [])
  const [clearGeneration, setClearGeneration] = useState(0)
  const context = useMemo(() => ({ reportPending, clearGeneration }), [reportPending, clearGeneration])
  const clearAll = () => {
    onClearAll()
    setClearGeneration((old) => old + 1)
  }

  // 外枠は付けない。Card・タブパネルの中に置かれることが多く、枠の入れ子で余白と線が二重になっていたため
  // 。開いた詳細絞り込みだけを薄い面で囲み、まとまりを示す
  return (
    <FilterBarContext value={context}>
      <div className={className}>
        <div
          className={cn(
            'flex flex-col gap-3 sm:flex-row',
            // ラベル付きの項目（ラベル + 入力の2段）とラベル無しのフリーワード欄・トグルが混在するので下端で揃える
            compactRow ? 'sm:flex-wrap sm:items-end' : 'sm:items-center',
          )}
        >
          {isSome(freeWord) && (
            <div
              className={cn(
                isSome(freeWordLabel) && 'flex flex-col gap-1.5',
                compactRow ? inlineFreeWordWidthClass : 'flex-1',
              )}
            >
              {isSome(freeWordLabel) && (
                <label htmlFor={freeWordInputId} className="text-xs font-medium text-muted-foreground">
                  {freeWordLabel}
                </label>
              )}
              <div className="relative">
                <Search className="pointer-events-none absolute top-1/2 left-3 z-10 size-4 -translate-y-1/2 text-muted-foreground" />
                <FilterTextInput
                  id={freeWordInputId}
                  value={freeWord}
                  onValueChange={(next) => {
                    onFreeWordChange?.(next)
                  }}
                  placeholder={freeWordPlaceholder}
                  // 隣に compact 系の項目（h-9）が並ぶときはその高さにする。フリーワードとトグルだけの行はトグルと揃える
                  className={cn(!compactRow && filterBarControlHeightClass, 'pl-9')}
                  aria-label={freeWordAriaLabel}
                  data-testid={`${testIdPrefix}-free-word`}
                  // clearable は FilterTextInput の既定（docs/develop/client_ui/components/form.md「入力の確定」）
                  clearButtonTestId={`${testIdPrefix}-free-word-clear`}
                />
              </div>
            </div>
          )}
          {hasDetail && inline && children}
          {primaryFilters}
          {hasDetail && props.expandable !== false && (
            <Button
              type="button"
              variant="outline"
              size="lg"
              // 主要項目と並ぶときは size="lg"（h-9）のまま compact 系の入力と揃える
              className={cn(!compactRow && filterBarControlHeightClass, 'justify-center gap-2')}
              aria-expanded={props.expanded}
              data-testid={`${testIdPrefix}-toggle`}
              onClick={() => {
                props.onExpandedChange(!props.expanded)
              }}
            >
              <SlidersHorizontal className="size-4" />
              詳細絞り込み
              {props.detailFilterCount > 0 && (
                <span className="ml-0.5 inline-flex min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-xs font-semibold text-primary-foreground">
                  {props.detailFilterCount}
                </span>
              )}
              <ChevronDown className={cn('size-4 transition-transform', props.expanded && 'rotate-180')} />
            </Button>
          )}
        </div>

        {chips.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {chips.map((chip) => (
              <span
                key={chip.key}
                className="inline-flex items-center gap-1.5 rounded-full bg-accent py-1 pr-1 pl-2.5 text-xs font-medium text-accent-foreground"
              >
                <span className="text-muted-foreground">{chip.label}:</span>
                {chip.value}
                <button
                  type="button"
                  onClick={() => {
                    onRemoveChip(chip.key)
                  }}
                  className="flex size-4 items-center justify-center rounded-full text-accent-foreground/70 hover:bg-primary/15 hover:text-accent-foreground"
                  aria-label={`${chip.label}の条件を削除`}
                >
                  <X className="size-3" />
                </button>
              </span>
            ))}
            <button
              type="button"
              onClick={clearAll}
              className="ml-1 text-xs font-medium text-primary hover:underline"
              data-testid={`${testIdPrefix}-clear-all`}
            >
              すべてクリア
            </button>
          </div>
        )}

        {/* 閉じてもアンマウントせず隠すだけにする。入力待ちの文字列・不正値のエラーは各入力欄が持っているため、
            アンマウントすると閉じた瞬間に消え、待機中の適用も取り消されてしまう */}
        {hasDetail && props.expandable !== false && (
          <div hidden={!props.expanded} className="mt-3 rounded-xl border border-border bg-muted/40 p-4">
            {children}
          </div>
        )}
      </div>
    </FilterBarContext>
  )
}
